/**
 * The write behind one form's question list: the list on screen, the
 * form the server last answered with, and the one commit that sends the
 * first when it differs from the second.
 *
 * It is not `useSettingCommit`, though it is the same save model
 * (DESIGN.md): a list's write runs the generate step and takes seconds,
 * and the screen keeps moving under it — nothing locks, nothing spins.
 * So the write is single-flight and in order. A commit while one is out
 * does not start a second; it marks the list to be looked at again when
 * the first lands, and whatever the list is then goes in one write. The
 * refs are what that next write reads, which is why an edit made while
 * one is in flight is sent after it rather than lost under it.
 */
import { useRef, useState } from "react";
import type {
  InquiryQuestionForm,
  InquiryQuestionSentence,
} from "../../lib/settings-api";
import { errorMessage } from "../../lib/error-message";
import { adopt, isRefusal, sameSentences, sentencesOf, toSend } from "./question-list";

export interface QuestionListSave {
  /** The form as the server last wrote it. */
  form: InquiryQuestionForm;
  /** The list on screen, edits and all. */
  draft: InquiryQuestionSentence[];
  /** Replaces the list on screen; the next commit reads it at once. */
  setDraft: (next: InquiryQuestionSentence[]) => void;
  /** Why the last write failed, until one lands. */
  error: string | null;
  /**
   * Sends the list if it differs from what the server holds. One write
   * at a time, in order; a change made during one is sent after it. A
   * blank line is a half-finished edit, not a question, and is not sent.
   */
  commit: () => Promise<void>;
}

export function useQuestionListSave(
  initial: InquiryQuestionForm,
  save: (sentences: InquiryQuestionSentence[]) => Promise<InquiryQuestionForm>,
): QuestionListSave {
  const [form, setFormState] = useState(initial);
  const [draft, setDraftState] = useState(() => sentencesOf(initial.questions));
  const [error, setError] = useState<string | null>(null);

  const latest = useRef(draft);
  const saved = useRef(form);
  const inFlight = useRef(false);
  const queued = useRef(false);

  const setDraft = (next: InquiryQuestionSentence[]) => {
    latest.current = next;
    setDraftState(next);
  };
  const setForm = (next: InquiryQuestionForm) => {
    saved.current = next;
    setFormState(next);
  };

  const commit = async () => {
    if (inFlight.current) {
      queued.current = true;
      return;
    }
    inFlight.current = true;
    try {
      do {
        queued.current = false;
        const sent = latest.current;
        const sentences = toSend(sent);
        if (sameSentences(sentences, sentencesOf(saved.current.questions))) {
          break;
        }
        try {
          const next = await save(sentences);
          setForm(next);
          setDraft(adopt(latest.current, sent, next.questions));
          setError(null);
        } catch (err) {
          setError(errorMessage(err));
          // Refused, the screen goes back to what the server holds, and
          // the refused change is not sent again under the next edit. An
          // edit made while the write was out goes with it; it was made
          // on top of a list that was never saved.
          if (isRefusal(err)) setDraft(sentencesOf(saved.current.questions));
          break;
        }
      } while (queued.current);
    } finally {
      inFlight.current = false;
    }
  };

  return { form, draft, setDraft, error, commit };
}
